<div class="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm" data-test="contact-wizard">

  <!-- Starting -->
  @if (starting()) {
    <div class="flex items-center justify-center py-12" data-test="wizard-starting">
      <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
           xmlns="http://www.w3.org/2000/svg">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
      </svg>
      <span class="sr-only">Verknüpfung wird vorbereitet…</span>
    </div>
  }

  <!-- The start call failed. Two shapes, deliberately kept apart: a channel that cannot be used at all has no
       retry worth offering, so it names who can fix it instead of a button whose only outcome is this card. -->
  @if (!starting() && errorMessage()) {
    <div class="flex items-start gap-3" [attr.data-test]="unavailable() ? 'wizard-unavailable' : 'wizard-error'"
         role="status">
      <svg class="h-5 w-5 shrink-0 mt-0.5" [class.text-yellow-600]="unavailable()"
           [class.text-red-500]="!unavailable()" fill="none" stroke="currentColor" viewBox="0 0 24 24"
           xmlns="http://www.w3.org/2000/svg">
        <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
      </svg>
      <div class="min-w-0">
        <p class="text-sm font-bold tracking-tight" [class.text-yellow-900]="unavailable()"
           [class.text-red-600]="!unavailable()">
          @if (unavailable()) { {{ label }} steht nicht zur Verfügung } @else { Es hat nicht geklappt }
        </p>
        <p class="mt-1 text-sm leading-relaxed" [class.text-yellow-800]="unavailable()"
           [class.text-gray-600]="!unavailable()">{{ errorMessage() }}</p>
        @if (!unavailable()) {
          <button type="button" (click)="start()" data-test="wizard-retry"
                  class="mt-2 inline-flex items-center rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-600 transition-all duration-200 hover:bg-red-200">
            Erneut versuchen
          </button>
        }
        @if (dismissable()) {
          <button type="button" (click)="dismiss()" data-test="wizard-dismiss-error"
                  class="mt-2 ml-2 inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 transition-all duration-200 hover:bg-gray-200">
            Schließen
          </button>
        }
      </div>
    </div>
  }

  <!-- The live challenge -->
  @if (!starting() && !errorMessage()) {
   @if (challenge(); as active) {

    <div class="flex flex-wrap items-start justify-between gap-2">
      <div class="min-w-0">
        <h2 class="text-base font-bold text-gray-900 tracking-tight" data-test="wizard-heading">
          @if (purpose() === Purpose.Reverify) {
            {{ label }} erneut bestätigen
          } @else {
            {{ label }} mit Ihrem Konto verknüpfen
          }
        </h2>
        @if (active.botUsername) {
          <p class="mt-0.5 text-xs text-gray-500">Bot: <span class="font-mono">{{ active.botUsername }}</span></p>
        }
      </div>
      @if (remaining && waiting) {
        <p class="text-xs font-semibold text-gray-500 tabular-nums" data-test="wizard-countdown"
           aria-live="polite">Code gültig noch {{ remaining }}</p>
      }
    </div>

    <!-- The server's guide. `kind` decides the shape; an unknown kind falls through to prose rather than
         vanishing, so a newer server can add a step this build has never heard of without breaking the flow. -->
    <ol class="mt-4 flex flex-col gap-4" data-test="wizard-steps">
      @for (step of steps; track $index) {
        <li class="flex items-start gap-3">
          <span aria-hidden="true"
                class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-theme-primary-600 text-xs font-bold text-white">{{ $index + 1 }}</span>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-bold text-gray-800 tracking-tight">{{ step.title }}</p>
            @if (step.text) {
              <p class="mt-0.5 text-sm text-gray-600 leading-relaxed">{{ step.text }}</p>
            }

            @switch (step.kind) {
              @case (StepKind.CopyCode) {
                <div class="mt-2 flex flex-wrap items-center gap-2">
                  <code data-test="wizard-code"
                        class="select-all rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 font-mono text-lg font-bold tracking-[0.25em] text-gray-900 break-all">{{ step.code || active.code }}</code>
                  <button type="button" (click)="copyCode()" data-test="wizard-copy"
                          class="inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
                    @if (copied()) { Kopiert } @else { Code kopieren }
                  </button>
                </div>
                @if (copyFailed()) {
                  <p class="mt-1 text-xs font-semibold text-yellow-800" data-test="wizard-copy-failed">
                    Der Code konnte nicht automatisch kopiert werden. Bitte markieren Sie ihn und kopieren Sie
                    ihn von Hand.
                  </p>
                }
              }

              @case (StepKind.OpenLink) {
                @if (step.link || active.deepLink) {
                  <a [href]="step.link || active.deepLink" target="_blank" rel="noopener noreferrer"
                     data-test="wizard-open-link"
                     class="mt-2 inline-flex items-center justify-center gap-2 rounded-2xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-theme-primary-700 active:scale-95">
                    {{ label }} öffnen
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M14 5h5m0 0v5m0-5L10 14M5 9v10h10" stroke-linecap="round" stroke-linejoin="round"
                            stroke-width="1.8"/>
                    </svg>
                  </a>
                }
              }

              @case (StepKind.WaitForBot) {
                <div class="mt-2 flex items-start gap-2" data-test="wizard-wait" aria-live="polite">
                  @if (waiting) {
                    <svg class="h-5 w-5 shrink-0 animate-spin text-theme-primary-500" fill="none"
                         viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                              stroke-width="4"></circle>
                      <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
                    </svg>
                  }
                  <p class="text-sm text-gray-600 leading-relaxed">
                    @switch (state()) {
                      @case (ChallengeState.AwaitingShareConsent) {
                        <span data-test="wizard-consent">Bitte bestätigen Sie im Chat, dass dieser Kontakt für
                          Ihren Arbeitgeber freigegeben werden darf.</span>
                      }
                      @case (ChallengeState.Completed) {
                        <span>Verknüpft.</span>
                      }
                      @default {
                        <span>Warten auf Ihre Bestätigung im Messenger…</span>
                      }
                    }
                  </p>
                </div>
              }

              @default {
                <!-- `info`, and every kind this build does not know: the title and text above already carry it. -->
              }
            }
          </div>
        </li>
      }
    </ol>

    <!-- Terminal states. Each names its own next action; none of them is an "error". -->
    @switch (state()) {
      @case (ChallengeState.Completed) {
        <div class="mt-4 flex items-start gap-3 rounded-2xl border border-green-100 bg-green-50 p-3"
             data-test="wizard-completed" role="status">
          <svg class="h-5 w-5 shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"
               xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
          <p class="text-sm font-semibold text-green-800">
            @if (purpose() === Purpose.Reverify) {
              {{ label }} wurde erneut bestätigt.
            } @else {
              {{ label }} ist jetzt mit Ihrem Konto verknüpft.
            }
          </p>
        </div>
      }

      @case (ChallengeState.Declined) {
        <div class="mt-4 rounded-2xl border border-yellow-100 bg-yellow-50 p-3" data-test="wizard-declined"
             role="status">
          <p class="text-sm font-bold text-yellow-900 tracking-tight">Freigabe abgelehnt</p>
          <p class="mt-1 text-sm text-yellow-800 leading-relaxed">
            Sie haben die Freigabe abgelehnt. Ohne Ihre Zustimmung kann Ihr Arbeitgeber diesen Kontakt nicht
            verwenden — die Anforderung bleibt deshalb bestehen.
          </p>
          <button type="button" (click)="start()" data-test="wizard-declined-retry"
                  class="mt-2 inline-flex items-center rounded-md bg-yellow-100 px-3 py-1.5 text-sm font-medium text-yellow-900 transition-all duration-200 hover:bg-yellow-200">
            Erneut versuchen
          </button>
        </div>
      }

      @case (ChallengeState.Expired) {
        <div class="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-3" data-test="wizard-expired"
             role="status">
          <p class="text-sm font-bold text-gray-800 tracking-tight">Code abgelaufen</p>
          <p class="mt-1 text-sm text-gray-600 leading-relaxed">
            Dieser Code ist nicht mehr gültig. Fordern Sie einen neuen an und senden Sie ihn erneut an den Bot.
          </p>
          <button type="button" (click)="start()" data-test="wizard-new-code"
                  class="mt-2 inline-flex items-center rounded-md bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-300">
            Neuen Code anfordern
          </button>
        </div>
      }
    }

    @if (dismissable() && state() !== ChallengeState.Completed) {
      <button type="button" (click)="dismiss()" data-test="wizard-dismiss"
              class="mt-4 inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
        Abbrechen
      </button>
    }
   }
  }
</div>
